// Add an alarm from the recipe library (UI-3). Two steps: (1) pick a recipe
// card, (2) fill the shared AlarmDefinitionForm (prefilled from template
// defaults) and POST. A mutation here recompiles the boat's ruleset and — for a
// delivery-opted boat — pushes it sub-second, so this really changes what the
// boat watches. `compileStatus: 'pending'` is HONEST-ALLOWED (a known recipe the
// compiler can't bind yet), surfaced plainly on the destination rather than
// treated as a failure. A duplicate active recipe 409s; we surface an inline
// error that links straight to the existing alarm's detail.

import { parseRecipeId } from '@neomaris/rules';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../api';
import type { DeliveryState } from '../lib/alarmDisplay';
import { AlarmDefinitionForm } from '../components/alarms/AlarmDefinitionForm';
import { RecipePicker } from '../components/alarms/RecipePicker';
import { useBoatChrome } from '../components/app/boatChrome';
import { boatUpdateNeededMessage } from '../lib/alarmInlineEditor';
import { initFromTemplate, toCreateBody, type AlarmDraft } from '../lib/alarmDefsForm';
import { observationLookup } from '../lib/alarmLive';
import { useAlertTemplates, type AlertTemplate } from '../lib/alertTemplates';
import { isRecipeWithdrawnError, mapCreateError } from '../lib/customAlarmErrors';
import { groupRecipes } from '../lib/recipeAvailability';
import type { Signal } from '../lib/signalCatalog';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useSignalCatalog } from '../lib/useSignalCatalog';
import { useUnits } from '../lib/useUnits';

interface CreateResponse {
  id: string;
  compileStatus: string | null;
  rulesetVersion: number | null;
  delivery?: { state?: DeliveryState };
}

interface DuplicateState {
  recipeTitle: string;
  existingId: string | null;
  /**
   * Set when the refusal was NOT simply "you already have this one" — today,
   * when the owner pinned a DIFFERENT instance and the boat's software is too
   * old to hold two of a recipe. Without it the banner says "already set up on
   * this boat" over a form where they just picked a different tank, which reads
   * as the product being broken rather than the boat needing an update.
   */
  reason: string | null;
}

export function AlarmNew() {
  const { boatId, boat, alerts, liveSamples } = useBoatChrome();
  const navigate = useNavigate();
  useDocumentTitle(boat ? `${boat.display_name} — Add alarm` : 'Add alarm — Neomaris');
  // The account's display units (ADR 0189). The draft carries them, so every
  // threshold box on the form below reads and writes in the owner's unit and the
  // POST body converts back exactly once.
  const units = useUnits();

  const templatesState = useAlertTemplates();
  const [template, setTemplate] = useState<AlertTemplate | null>(null);
  const [draft, setDraft] = useState<AlarmDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateState | null>(null);
  const [query, setQuery] = useState('');

  // What this boat actually reports — the same accepted set the compiler binds
  // against, so "your boat isn't reporting this" is the compiler's own verdict
  // rather than a guess made on the page.
  const catalog = useSignalCatalog(boatId);

  // The reading beside each threshold box (ADR 0170). There is no compiled rule
  // here — the alarm does not exist yet — so the form falls back to whichever
  // instance the operator pins, and shows nothing until they pin one.
  const catalogByPath = useMemo(
    () => new Map<string, Signal>(catalog.signals.map((s) => [s.path, s])),
    [catalog.signals]
  );
  const observationFor = useMemo(
    () => observationLookup({ live: liveSamples, catalog: catalogByPath }),
    [liveSamples, catalogByPath]
  );

  // Client-side filter over the recipe library. Matching the title, description
  // and recipe id covers both how an owner would say it ("bilge") and how the
  // library names it. Nothing matching is not a dead end — it is the moment to
  // offer the custom builder.
  const visibleTemplates = useMemo(() => {
    if (templatesState.kind !== 'ready') return [];
    const terms = query
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    if (terms.length === 0) return templatesState.templates;
    return templatesState.templates.filter((t) => {
      const hay = `${t.title} ${t.description} ${t.recipeId}`.toLowerCase();
      return terms.every((term) => hay.includes(term));
    });
  }, [templatesState, query]);

  // Split what is left into what this boat can use and what it cannot. Search
  // runs FIRST and reaches both groups: an owner who types "bilge" is looking
  // for the bilge recipe, and answering "no results" while it sits collapsed
  // three inches below would be a lie the page could see was false.
  // `core_version` is the fourth argument because a recipe minted after this
  // boat's software was built is one its bundled compiler has never heard of —
  // the alarm would be stored and watch nothing, so the server refuses it and
  // the page has to say so rather than offer it.
  const groups = useMemo(
    () =>
      groupRecipes(
        visibleTemplates,
        catalog.signals,
        catalog.status === 'ready',
        boat?.core_version ?? null
      ),
    [visibleTemplates, catalog.signals, catalog.status, boat?.core_version]
  );
  const reasonByRecipe = useMemo(
    () => new Map(groups.unavailable.map((u) => [u.template.recipeId, u.reason])),
    [groups.unavailable]
  );
  // Searching opens the drawer: the match the owner typed must be on screen, not
  // one click away behind a summary that does not mention it.
  const searching = query.trim().length > 0;

  // Catalog BASE recipe id → what this boat already has for it.
  //
  // Keyed on the base, not the stored id, because a per-instance alarm stores
  // `<base>#<pinnedPath>` (task recipe_per_instance): three tank alarms are
  // three different stored ids but one catalog recipe, and the picker card is
  // per catalog recipe. `count` drives honest wording — "Added" on a recipe that
  // can still take another instance reads as "you already have this, don't",
  // which is now wrong. `firstId` is only useful for linking when there is
  // exactly one, so the link site checks the count.
  const existingByRecipe = useMemo(() => {
    const map = new Map<string, { count: number; firstId: string }>();
    if (alerts.state.kind === 'ready') {
      for (const row of alerts.state.response.alerts) {
        const { base } = parseRecipeId(row.definition.recipeId);
        const prev = map.get(base);
        if (prev === undefined) map.set(base, { count: 1, firstId: row.definition.id });
        else prev.count += 1;
      }
    }
    return map;
  }, [alerts.state]);

  const backToAlarms = `/dashboard/boats/${boatId}/alarms`;

  function pick(t: AlertTemplate): void {
    setTemplate(t);
    setDraft(initFromTemplate(t, units));
    setServerError(null);
    setDuplicate(null);
  }

  function backToPicker(): void {
    setTemplate(null);
    setDraft(null);
    setServerError(null);
    setDuplicate(null);
  }

  async function save(finalDraft: AlarmDraft): Promise<void> {
    if (busy) return;
    setBusy(true);
    setServerError(null);
    setDuplicate(null);
    try {
      const res = await api<CreateResponse>(`/v1/boats/${boatId}/alerts`, {
        method: 'POST',
        body: toCreateBody(finalDraft),
        idempotencyKey: crypto.randomUUID(),
      });
      // Refresh the shared snapshot so the destination detail page finds the new
      // definition, then navigate — carrying the honest just-saved outcome.
      alerts.refresh();
      navigate(`/dashboard/boats/${boatId}/alarms/${res.id}`, {
        state: {
          justSaved: {
            kind: 'created',
            compileStatus: res.compileStatus,
            rulesetVersion: res.rulesetVersion,
            // Whether the boat actually GOT it — the create path needs this as
            // much as the edit path does (ADR 0179).
            delivery: res.delivery?.state ?? null,
          },
        },
      });
    } catch (err) {
      const e = err as { status?: number; body?: { error?: string; message?: string } };
      if (e.status === 409) {
        // Only offer "open the existing alarm" when there IS one to open. With
        // several instances of this recipe, linking to an arbitrary one of them
        // would send the owner to an alarm they were not talking about; the
        // server's message already names the instance that collided.
        const existing = template ? existingByRecipe.get(template.recipeId) : undefined;
        // Carry the server's own sentence through when it explains something the
        // canned wording cannot. It is the API's message, not free text from the
        // boat, so it is safe to show.
        const serverMessage = typeof e.body?.message === 'string' ? e.body.message : null;
        setDuplicate({
          recipeTitle: template?.title ?? 'this recipe',
          existingId: existing !== undefined && existing.count === 1 ? existing.firstId : null,
          reason: serverMessage !== null && /software/i.test(serverMessage) ? serverMessage : null,
        });
      } else if (e.status === 400) {
        setServerError('That alarm couldn’t be saved — please check the values and try again.');
      } else if (e.status === 422 && e.body?.error === 'recipe_needs_boat_update') {
        // The picker disables these cards, so reaching this needs a stale
        // library, a boat that downgraded, or a direct POST. Answer it honestly
        // anyway: the two 422s want opposite actions, and telling someone to
        // pick a different signal when the signal was never the problem is the
        // kind of wrong advice that costs an afternoon.
        setServerError(boatUpdateNeededMessage(e));
      } else if (isRecipeWithdrawnError(e)) {
        // The library hides withdrawn recipes, so reaching this needs a stale
        // library or a direct link (TOM-136). The server's sentence says why the
        // alarm is gone; the pinned-instance advice below would be wrong here.
        setServerError(mapCreateError(e).message);
      } else if (e.status === 422) {
        // The pinned instance stopped reporting while this form was open. The
        // pin is hard on purpose (it never re-points at a sibling), so the cloud
        // refuses the alarm rather than storing one that would sit pending —
        // and retrying the same body cannot help. Say what to do instead.
        setServerError(
          'Your boat has stopped reporting the one you picked, so this alarm can’t watch it. Choose another, or switch to Automatic.'
        );
      } else {
        setServerError('Something went wrong saving this alarm. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  const backLink = (
    <Link className="detail-back" to={backToAlarms}>
      ← Back to alarms
    </Link>
  );

  return (
    <section data-testid="alarm-new">
      {template === null ? (
        backLink
      ) : (
        <button type="button" className="detail-back detail-back--btn" onClick={backToPicker}>
          ← Choose a different alarm
        </button>
      )}

      <div className="page-head">
        <h1>{template === null ? 'Add an alarm' : template.title}</h1>
        <p className="page-sub">
          {template === null ? 'Pick what you want your boat to watch for.' : template.description}
        </p>
      </div>

      {templatesState.kind === 'loading' ? (
        <p className="muted">Loading the alarm library…</p>
      ) : null}
      {templatesState.kind === 'error' ? (
        <ul className="form-errors">
          <li>{templatesState.message}</li>
        </ul>
      ) : null}

      {templatesState.kind === 'ready' && template === null ? (
        <>
          {/* The way out of the recipe set: anything the boat reports, on your
              own numbers. Deliberately NOT a recipe card — it is a different
              kind of choice, and burying it in the grid hid it. */}
          <Link
            className="alarm-custom-entry"
            to={`${backToAlarms}/new/custom`}
            data-testid="alarm-new-custom-entry"
          >
            <span className="alarm-custom-entry__main">
              <span className="alarm-custom-entry__title">Build a custom alarm</span>
              <span className="alarm-custom-entry__sub">
                Not in the list? Watch any signal your boat reports — an engine reading, a tank, a
                battery, a switch — on numbers you choose.
              </span>
            </span>
            <span className="alarm-custom-entry__go" aria-hidden="true">
              Start →
            </span>
          </Link>

          <div className="recipe-search">
            <label className="recipe-search__label" htmlFor="alarm-new-search">
              Search ready-made alarms
            </label>
            <input
              id="alarm-new-search"
              className="form-input"
              type="search"
              value={query}
              placeholder="Try “battery”, “bilge”, “anchor”"
              autoComplete="off"
              data-testid="alarm-new-search"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {groups.available.length > 0 ? (
            <RecipePicker
              templates={groups.available}
              existingByRecipe={existingByRecipe}
              onPick={pick}
            />
          ) : null}

          {/* Everything this boat cannot use today, one click away. Kept on the
              page rather than deleted from it: a bilge switch you have not wired
              yet is one you may be about to wire, and a recipe that simply
              vanished would read as a missing feature. Each card carries the
              specific reason, because "we can't run this yet" is our problem and
              "your boat isn't reporting this" is a sensor. */}
          {groups.unavailable.length > 0 ? (
            <details
              className="recipe-unavailable"
              open={searching || groups.available.length === 0}
              data-testid="alarm-new-unavailable"
            >
              <summary data-testid="alarm-new-unavailable-summary">
                {groups.unavailable.length} {groups.unavailable.length === 1 ? 'alarm' : 'alarms'}{' '}
                your boat can’t use yet
              </summary>
              <p className="muted recipe-unavailable__lead">
                These need a signal your boat isn’t reporting, or something we haven’t built yet.
                You can still set one up — it just won’t watch anything until the signal arrives.
              </p>
              <RecipePicker
                templates={groups.unavailable.map((u) => u.template)}
                existingByRecipe={existingByRecipe}
                onPick={pick}
                reasonByRecipe={reasonByRecipe}
                testId="recipe-picker-unavailable"
              />
            </details>
          ) : null}

          {groups.available.length === 0 && groups.unavailable.length === 0 ? (
            <div className="recipe-empty" data-testid="alarm-new-no-results">
              <p className="recipe-empty__line">
                {searching
                  ? `No ready-made alarm matches “${query.trim()}”.`
                  : 'There are no ready-made alarms to show.'}
              </p>
              <p className="muted">
                You can still watch it: the custom builder works on anything your boat reports.
              </p>
              <Link className="btn btn-primary" to={`${backToAlarms}/new/custom`}>
                Build a custom alarm
              </Link>
            </div>
          ) : null}
        </>
      ) : null}

      {template !== null && draft !== null ? (
        <>
          {duplicate !== null ? (
            <div className="alarm-command-banner" role="alert" data-testid="alarm-new-duplicate">
              {duplicate.reason !== null ? (
                <>
                  {duplicate.recipeTitle} is already set up on this boat, and watching a second one
                  needs newer software on the boat.{' '}
                </>
              ) : (
                <>{duplicate.recipeTitle} is already set up on this boat. </>
              )}
              {duplicate.existingId !== null ? (
                <Link to={`${backToAlarms}/${duplicate.existingId}`}>Open the existing alarm</Link>
              ) : (
                <Link to={backToAlarms}>See your alarms</Link>
              )}{' '}
              to change it.
            </div>
          ) : null}
          <AlarmDefinitionForm
            mode="create"
            draft={draft}
            onDraftChange={setDraft}
            onSave={(d) => void save(d)}
            onCancel={backToPicker}
            busy={busy}
            serverError={serverError}
            submitLabel="Add alarm"
            pendingLabel="Adding…"
            observationFor={observationFor}
            vesselType={
              alerts.state.kind === 'ready' ? (alerts.state.response.vesselType ?? null) : null
            }
          />
        </>
      ) : null}
    </section>
  );
}
